import { css } from '@emotion/react';
import {
  type ComponentProps, type PropsWithChildren,
} from 'react';
import { TriStateCheckboxComponent } from '~/_shared/baseComponents/checkbox';
import { LinkComponent } from '~/_shared/baseComponents/links';
import type { TriStateRange } from '~/_shared/constants/triStateRange.enum';
import type { Theme } from '~/_shared/themes/theme.model';
import { noop } from '~/_shared/utils/function.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import {
  type ActiveBoundaryFillDropdownProps, ActiveBoundaryFillHeader,
} from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/activeBoundaryFillIHeader';
import {
  dataLayersCheckboxStyle, dataLayersOptionStyle,
  dataLayerUpgradeLinkStyle,
} from '~/sidebar/sidebarApps/mapTools/dataLayersTool/dataLayersTool.styles';
import { ActiveDataLayerToolbarComponent } from '~/sidebar/sidebarApps/mapTools/dataLayersTool/listing/activeDataLayerToolbar.component';
import { BoundaryKeyHeaderInfoComponent } from '~/sidebar/sidebarApps/rightSidebar/masterKey/boundaryKey/BoundaryKeyHeaderInfo.component';
import type { DataLayer } from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.state';

const nonPurchasedContainerStyle = (theme: Theme) => css({
  border: `2px solid ${theme.backgroundColors.warning}`,
});

const headerActionsWrapperStyle = (theme: Theme) => css({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: theme.backgroundColors.secondary,
  boxSizing: 'border-box',
  padding: '5px 10px 5px 16px',
});

const headerToolbarWrapperStyle = css({
  minHeight: 34,
});

const subheaderStyle = (theme: Theme) => css({
  backgroundColor: theme.backgroundColors.highlight,
  boxSizing: 'border-box',
  fontSize: 14,
  paddingBlock: 4,
  paddingInline: 12,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  gap: 8,
  userSelect: 'none',
  minHeight: 32,
});

const warningNoteStyle = (theme: Theme) => css({
  color: theme.textColors.secondary,
  padding: '8px 12px',
  fontSize: 14,
});

const upgradeStyle = (theme: Theme) => css({
  color: theme.textColors.warning,
  textTransform: 'lowercase',
  fontSize: 12,
  width: '100%',
  textAlign: 'right',
  padding: 0,
  boxSizing: 'border-box',
});

export const ActiveDataLayerComponent = ({
  boundaryGroupName,
  children,
  dataLayer,
  dropdownProps,
  hasInaccessibleDemographics,
  hasNonpurchasedDemographics,
  toolbarProps,
  showAllCheckmark,
  isCurrentDemographicPurchased,
  onToggleShowAll,
}: PropsWithChildren<Readonly<{
  boundaryGroupName?: string;
  dataLayer: DataLayer;
  toolbarProps: ComponentProps<typeof ActiveDataLayerToolbarComponent>;
  dropdownProps: ActiveBoundaryFillDropdownProps;
  hasInaccessibleDemographics?: boolean;
  hasNonpurchasedDemographics?: boolean;
  showAllCheckmark: TriStateRange;
  isCurrentDemographicPurchased: boolean;
  onToggleShowAll?: () => void;
}>>) => {
  const [t] = useTranslation();

  return (
    <div css={hasNonpurchasedDemographics || hasInaccessibleDemographics || toolbarProps.onPurchase ? nonPurchasedContainerStyle : undefined}>
      <ActiveBoundaryFillHeader
        dropdownProps={dropdownProps}
      />
      <div css={subheaderStyle}>
        <BoundaryKeyHeaderInfoComponent
          shortenedSubEntity={dataLayer.data.title}
          boundaryTerritoryGroupName={boundaryGroupName}
        />
        {toolbarProps.onPurchase && (
          <div
            css={upgradeStyle}
          >
            <LinkComponent
              css={dataLayerUpgradeLinkStyle}
              path={null}
              onClick={toolbarProps.onPurchase}
            >
              {t('Upgrade')}
            </LinkComponent>
          </div>
        )}
      </div>
      <div css={headerActionsWrapperStyle}>
        <div css={dataLayersOptionStyle}>
          <TriStateCheckboxComponent
            isDisabled={!onToggleShowAll}
            checkmark={showAllCheckmark}
            checkedSetter={onToggleShowAll ?? noop}
            text={t('Show All')}
            textStyle={dataLayersCheckboxStyle}
          />
        </div>
        <div css={headerToolbarWrapperStyle}>
          <ActiveDataLayerToolbarComponent {...toolbarProps} />
        </div>
      </div>
      {hasInaccessibleDemographics && (
        <div css={warningNoteStyle}>
          {t('dataLayersTool.containsInaccessibleDemographicsWarning')}
        </div>
      )}
      {!hasInaccessibleDemographics && hasNonpurchasedDemographics && isCurrentDemographicPurchased && (
        <div css={warningNoteStyle}>
          {t('dataLayersTool.containsNonPurchasedDemographicsWarning.purchasedLayer')}
        </div>
      )}
      {!hasInaccessibleDemographics && hasNonpurchasedDemographics && !isCurrentDemographicPurchased && (
        <div css={warningNoteStyle}>
          {t('dataLayersTool.containsNonPurchasedDemographicsWarning.nonPurchasedLayer')}
        </div>
      )}
      {children}
    </div>
  );
};
